import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import OutsideClickHandler from "react-outside-click-handler";
import styles from "./ModalProduct.module.sass";
import Panel from "./Panel";
import Icon from "../../../components/Icon";
import cn from "classnames";

const ModalPreview = ({
  visible,
  onClose,
  video,
  gallery,
  download,
  title,
  figcaption,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const resetZoomAndPosition = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleClose = () => {
    resetZoomAndPosition();
    onClose();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomFactor = 0.1;
    const delta = e.deltaY > 0 ? -zoomFactor : zoomFactor;
    setZoomLevel((prevZoom) => Math.max(1, Math.min(5, prevZoom + delta)));
  };

  const escFunction = useCallback(
    (e) => {
      if (e.keyCode === 27) {
        handleClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-product");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  const handleZoomIn = () => {
    setZoomLevel((prevZoomLevel) => Math.min(5, prevZoomLevel + 0.2));
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomOut = () => {
    setZoomLevel((prevZoomLevel) => Math.max(1, prevZoomLevel - 0.2));
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition((prevPosition) => {
        const newX = prevPosition.x + e.movementX;
        const newY = prevPosition.y + e.movementY;
        const maxOffsetX = window.innerWidth * (zoomLevel - 1) / 2;
        const maxOffsetY = window.innerHeight * (zoomLevel - 1) / 2;
        return {
          x: Math.max(-maxOffsetX, Math.min(maxOffsetX, newX)),
          y: Math.max(-maxOffsetY, Math.min(maxOffsetY, newY)),
        };
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return createPortal(
    visible && (
      <div id="modal-product" className={styles.modal}>
        <div className={styles.outer}>
          <OutsideClickHandler onOutsideClick={handleClose}>
            <Panel
              title={title}
              onClose={handleClose}
              download={download}
              galleryPanel={gallery}
            />
            {gallery && (
              <div className={styles.wrapper}>
                  {gallery.map((x, index) => (
                    <div key={index} className={styles.slide}>
                      <div 
                        className={styles.preview} 
                        style={{
                          position: 'relative',
                          width:"100vw",
                          height: '96vh',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                        onWheel={handleWheel}
                      >
                        <img
                          src={x}
                          alt="Gallery"
                          style={{
                            maxWidth: '100vw',
                            maxHeight: '100vh',
                            objectFit: 'contain',
                            transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                            cursor: zoomLevel > 1 ? "grab" : "zoom-in",
                            transition: isDragging
                              ? "none"
                              : "transform 0.1s ease-out",
                          }}
                          onMouseDown={handleMouseDown}
                          onMouseMove={handleMouseMove}
                          onMouseUp={handleMouseUp}
                          onMouseLeave={handleMouseUp}
                        />
                      </div>
                    </div>
                  ))}
                <div
                  className={styles.figcaption}
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#272B30',
                    borderRadius: '20px',
                    padding: '10px',
                    opacity: '0.7',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex justify-center">
                      <button
                        className={cn(
                          "text-white hover:text-gray-400 transition-colors duration-300 mr-2",
                          styles.Zoomin
                        )}
                        onClick={handleZoomIn}
                      >
                        <Icon name="zoomin" size="24" />
                      </button>
                      <button
                        className={cn(
                          "text-white hover:text-gray-400 transition-colors duration-300",
                          styles.Zoomin
                        )}
                        onClick={handleZoomOut}
                      >
                        <Icon name="zoomOut" size="24" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </OutsideClickHandler>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalPreview;